import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import hero from "@/assets/hero.jpg";
import { API_BASE_URL } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1] as const;
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, delay, ease: EASE },
});

export function Hero() {
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/settings`);
        if (res.ok) return await res.json();
      } catch (err) {
        // silent fallback
      }
      return null;
    }
  });

  const eyebrow = settings?.heroEyebrow || "Royal Lumina · Est. 2019";
  const title = settings?.heroTitle || "Handcrafted Luxury Scented Candles";
  const italic = settings?.heroItalic || "for Your Home.";
  const desc = settings?.heroDesc || "Handcrafted scented candles poured in small batches — made to inspire peace, love and quiet luxury in every room.";
  const stat1 = settings?.heroStat1 || "50K+";
  const stat1Lbl = settings?.heroStat1Lbl || "Happy Homes";
  const stat2 = settings?.heroStat2 || "4.9★";
  const stat2Lbl = settings?.heroStat2Lbl || "Avg Rating";
  const stat3 = settings?.heroStat3 || "200+";
  const stat3Lbl = settings?.heroStat3Lbl || "Unique Scents";

  return (
    <section className="relative -mt-20 min-h-[100svh] overflow-hidden bg-primary text-cream">
      {/* Background image */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.img
          src={hero}
          alt="Royal Lumina luxury candle"
          className="h-full w-full object-cover opacity-90 will-change-transform"
          width={1600}
          height={1600}
          fetchPriority="high"
          loading="eager"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 3.2, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-primary/40" />
      </div>

      {/* Floating particles */}
      <div className="pointer-events-none absolute inset-0">
        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={i}
            className="absolute block h-1 w-1 rounded-full bg-gold/70 animate-flicker"
            style={{
              top: `${(i * 53) % 100}%`,
              left: `${(i * 37) % 100}%`,
              animationDelay: `${(i % 6) * 0.4}s`,
              filter: "blur(1px)",
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-40 pb-24 min-h-[100svh] flex items-center">
        <div className="max-w-2xl">
          <motion.p className="eyebrow mb-6" {...fadeUp(0)}>{eyebrow}</motion.p>
          <motion.h1 className="font-display text-[clamp(2.75rem,6.5vw,5.5rem)] leading-[1.02] font-semibold tracking-tight" {...fadeUp(0.1)}>
            {title}<br />
            <span className="italic text-gold font-medium">{italic}</span>
          </motion.h1>
          <motion.p className="mt-8 max-w-md text-base md:text-lg text-cream/75 leading-relaxed" {...fadeUp(0.3)}>
            {desc}
          </motion.p>
          <motion.div className="mt-10 flex flex-wrap gap-4" {...fadeUp(0.5)}>
            <Link to="/shop" className="btn-gold group">
              Shop Now <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/collections" className="btn-outline-light">
              Explore Collection
            </Link>
          </motion.div>

          <motion.div className="mt-16 flex items-center gap-10 text-xs uppercase tracking-[0.25em] text-cream/60" {...fadeUp(0.7)}>
            <div>
              <div className="text-2xl font-display text-gold">{stat1}</div>
              <div className="mt-1">{stat1Lbl}</div>
            </div>
            <div className="h-8 w-px bg-cream/20" />
            <div>
              <div className="text-2xl font-display text-gold">{stat2}</div>
              <div className="mt-1">{stat2Lbl}</div>
            </div>
            <div className="h-8 w-px bg-cream/20 hidden sm:block" />
            <div className="hidden sm:block">
              <div className="text-2xl font-display text-gold">{stat3}</div>
              <div className="mt-1">{stat3Lbl}</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-cream/50 text-[0.65rem] tracking-[0.4em] uppercase">
        Scroll
      </div>
    </section>
  );
}